import { useLazyQuery } from "@apollo/client";
import { AntDesign } from "@expo/vector-icons";
import { Q } from "@nozbe/watermelondb";
import { useDatabase } from "@nozbe/watermelondb/hooks";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  Badge,
  Banner,
  Divider,
  FlatListSearchable,
  Page,
  Status,
  Text,
  useWithAppState,
} from "@towersystems/roam-common-native";
import useOnline from "@towersystems/roam-common-native/lib/utilities/use-online";
import { Sale as SaleModel } from "@towersystems/roam-common/lib/generated-types";
import React, { useCallback, useEffect, useState } from "react";
import { Colors, Spacings, TouchableOpacity, View } from "react-native-ui-lib";
import { ActionsRootParamList } from "../../../../types";
import { SyncState } from "../../../../utilities/data/constants";
import Sale, { SaleState } from "../../../../utilities/data/model/Sale";
import { formatDateToCalendar } from "../../../../utilities/date/format-date";
import { useLocalSettingsProvider } from "../../../../utilities/local-settings";
import { moneyToFormattedString } from "../../../../utilities/money";
import { useScheduler } from "../../../../utilities/scheduler";
import { useSwitchSale } from "../../utils/sale-controller/hooks";
import { QUERY_LIST_SALES } from "../../utils/sale/constants";
import { useActionBackOrClose } from "../../utils/use-action-back-or-close";
import { FlatListSearchableSkeleton } from "../../../../components/FlatListSearchableSkeleton";

export type SwitchSaleScreenProps = NativeStackScreenProps<
  ActionsRootParamList,
  "SwitchSaleScreen"
>;

export const SwitchSaleScreen = ({ navigation }: SwitchSaleScreenProps) => {
  const onlineConnection = useOnline();
  const {
    settings: { salesQueueEnabled },
  } = useLocalSettingsProvider();
  useActionBackOrClose(navigation);
  const [sales, setSales] = useState<SaleModel[] | undefined>();
  const [salesOffline, setSalesOffline] = useState<Sale[] | undefined>([]);
  const [loading, setLoading] = useState(true);

  const database = useDatabase();
  const { appState } = useWithAppState();
  const switchSale = useSwitchSale();
  const [term, setTerm] = useState("");
  const { schedule } = useScheduler(500);

  const [q] = useLazyQuery<{
    sales: { items: SaleModel[] };
  }>(QUERY_LIST_SALES);

  const isOnline = salesQueueEnabled && onlineConnection;

  useEffect(() => {
    if (isOnline) {
      schedule(() => {
        queryOnlineSales().catch((e) => console.log(e));
      });
    } else {
      schedule(() => {
        queryOfflineSales(term).catch((e) => console.log(e));
      });
    }
  }, [term, isOnline]);

  const queryOfflineSales = async (term: string) => {
    try {
      setLoading(true);
      const salesToFilter = await database.collections
        .get<Sale>("sales")
        .query(
          Q.where("invoice_number", Q.like(`%${term}%`)),
          Q.where("sync_state", Q.oneOf([SyncState.UPLOAD, SyncState.MODIFED])),
          Q.where("external_id", Q.eq(null)),
          Q.where("location_id", Q.eq(appState.locationId || "")),
          Q.where("id", Q.notEq(appState.localSaleId || "")),
          Q.where(
            "sale_state",
            Q.oneOf([
              SaleState.SAVED,
              SaleState.LAYBY_SAVED,
              SaleState.INVOICE_SAVED,
              SaleState.QUOTE_SAVED,
              SaleState.PROFORMA_SAVED,
            ])
          )
        )
        .fetch();
      setSalesOffline(salesToFilter);
    } catch (error) {}
    setLoading(false);
  };

  const queryOnlineSales = async () => {
    try {
      setLoading(true);
      const salesToFilter = (
        await database.collections
          .get<Sale>("sales")
          .query(
            Q.where(
              "sync_state",
              Q.oneOf([SyncState.UPLOAD, SyncState.MODIFED])
            )
          )
          .fetch()
      ).map((sale) => sale.id);

      const result = await q({
        variables: {
          options: {
            limit: 50,
            page: 1,
            sort: { createdAt: "DESC" },
            filter: {
              locationId: { eq: appState.locationId || "-" },
              state: {
                in: [
                  "SAVED",
                  "QUOTE_SAVED",
                  "INVOICE_SAVED",
                  "LAYBY_SAVED",
                  "PROFORMA_SAVED",
                ],
              },
              source: { eq: "WEB_REGISTER" },
              invoiceNumber: { contains: term },
            },
          },
        },
      });

      const sales = result.data?.sales.items.filter(
        (sale) => !salesToFilter.includes(sale.id)
      );

      setSales(sales);
    } catch (error) {}
    setLoading(false);
  };

  const handleOnSwitchToSale = async (sale: SaleModel | Sale) => {
    try {
      await switchSale(sale);
      navigation.getParent()?.goBack();
    } catch (e) {
      console.log(e);
    }
  };

  const items = isOnline ? sales : salesOffline;

  const renderItem = useCallback(
    ({ item: sale }: { item: SaleModel | Sale }) => {
      if (sale instanceof Sale) {
        return (
          <TouchableOpacity onPress={() => handleOnSwitchToSale(sale)}>
            <View>
              <View flex row style={{ justifyContent: "space-between" }}>
                <View>
                  <Text variant="heading" fontWeight="medium">
                    #{sale.invoiceNumber}
                  </Text>
                </View>

                <View row centerV centerH>
                  <AntDesign
                    style={{ marginLeft: Spacings.s4, color: Colors.Text }}
                    name="right"
                    size={24}
                  />
                </View>
              </View>
              {sale.note ? (
                <View style={{ marginTop: Spacings.s2 }}>
                  <Text variant="heading4" color="subdued">
                    "{sale.note}"
                  </Text>
                </View>
              ) : null}
            </View>
          </TouchableOpacity>
        );
      }

      return (
        <TouchableOpacity onPress={() => handleOnSwitchToSale(sale)}>
          <View>
            <View flex row style={{ justifyContent: "space-between" }}>
              <View>
                <Text variant="heading" fontWeight="medium">
                  #{sale.invoiceNumber}
                </Text>
                <Text variant="heading4" color="subdued">
                  {`${formatDateToCalendar(sale.saleDate)}, at ${
                    sale.location?.name || "No Location"
                  }`}
                </Text>
              </View>

              <View row centerV centerH>
                <View style={{ alignItems: "flex-end" }}>
                  <Text variant="heading" fontWeight="medium">
                    {`${moneyToFormattedString(sale.totalPrice)}`}
                  </Text>
                  <Text
                    style={{ opacity: 0.5, textAlign: "right" }}
                  >{`${sale.lineItems.length} items`}</Text>
                  <Badge style={{}} status={sale.state as Status}>
                    {sale.state}
                  </Badge>
                </View>
                <AntDesign
                  style={{ marginLeft: Spacings.s4, color: Colors.Text }}
                  name="right"
                  size={24}
                />
              </View>
            </View>

            {sale.note ? (
              <View style={{ marginTop: Spacings.s2 }}>
                <Text variant="heading4" color="subdued">
                  "{sale.note}"
                </Text>
              </View>
            ) : null}
          </View>
        </TouchableOpacity>
      );
    },
    []
  );

  const emptyContent =
    !loading && items?.length == 0 ? (
      <View flex centerH centerV>
        <Text>No parked sales</Text>
      </View>
    ) : null;

  const isOfflineWarning = !isOnline ? (
    <View style={{ marginBottom: Spacings.s4 }}>
      <Banner
        icon={<AntDesign name="warning" size={24} color={Colors.Text} />}
        title="You are offline or sales queue disabled!"
        status="warning"
      >
        <Text color="subdued">
          You can only switch to sales that have not been synced with the
          server.
        </Text>
      </Banner>
    </View>
  ) : null;

  if (loading && !sales) {
    return (
      <Page>
        <FlatListSearchableSkeleton />
      </Page>
    );
  }

  return (
    <Page titleContainerStyle={{ alignItems: "center" }}>
      {isOfflineWarning}
      <FlatListSearchable
        placeholder="Enter invoice number"
        value={term}
        onChange={(newTerm) => setTerm(newTerm)}
        ItemSeparatorComponent={() => <Divider />}
        data={items}
        renderItem={renderItem}
        style={{ height: "100%" }}
        scrollEnabled={true}
        ListEmptyComponent={emptyContent}
      ></FlatListSearchable>
    </Page>
  );
};
